import { FieldArray, getIn } from 'formik';
import { forEach, keys } from 'lodash';
import { useContext } from 'react';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { SecondaryButton } from '../../../../shared/Button';
import { FieldErrorIndicator } from '../../../../shared/form/FieldErrorIndicator';
import { FieldContainer } from '../../../../shared/form/FormField';
import { FormFieldRow } from '../../../../shared/form/FormFieldRow';
import { VerticalCheckboxField } from '../../../../shared/form/inputs/CheckboxField';
import { SelectOptions } from '../../../../shared/form/inputs/Select';
import { FastSelectField, SelectField } from '../../../../shared/form/inputs/SelectField';
import { FastTextInputField } from '../../../../shared/form/inputs/TextInputField';
import { spacing01, spacing02 } from '../../../../styling/spacing';
import { PumpFormModel } from '../GetDataForQuoteOrDesignForm';
import { QuoteOrDesignFormContext } from '../QuoteOrDesignFormContext';
import { ReactComponent as DeleteIcon } from '../../../../images/icons/delete.svg';
import {
  PumpLocationCode,
  pumpLocationSelectOptions,
  PumpSelectionTypeCode,
  pumpSelectionTypeSelectOptions,
  UnitHeatSourceTypeCode,
  unitHeatSourceTypeDisplayNames,
} from '../quoteOrDesignFormData';
import { manifoldFieldName, unitFieldName } from '../quoteOrDesignFormFieldNameHelpers';
import { CylinderVolumeField } from './CylinderVolumeField';
import { UnitFieldContext } from './UnitFieldContext';
import { addUnitPumpButtonTestId, deleteUnitPumpButtonTestId } from './UnitFields';
import { unitHasHeatPump, unitHasUnderfloorHeating } from './unitFormModel';

export const UnitDetailsFieldsRow = () => {
  const { unit, unitIndex } = assertNotNull(useContext(UnitFieldContext));

  const {
    referenceData: { unitPumpOptions },
    formProps: { setFieldValue, errors, touched },
    formType,
  } = assertNotNull(useContext(QuoteOrDesignFormContext));

  const heatSourceSelectOptions: SelectOptions<UnitHeatSourceTypeCode> = keys(unitHeatSourceTypeDisplayNames).map(
    heatSourceTypeCode => ({
      text: unitHeatSourceTypeDisplayNames[heatSourceTypeCode as UnitHeatSourceTypeCode],
      value: heatSourceTypeCode as UnitHeatSourceTypeCode,
    }),
  );

  const handlePumpSelectionTypeChange = (newPumpSelectionTypeCode: PumpSelectionTypeCode) => {
    if (newPumpSelectionTypeCode !== 'Automatic' || unit.pumpLocation === 'PumpOnManifold') {
      setFieldValue(unitFieldName(unitIndex, 'pumps'), [null]);
    }

    if (newPumpSelectionTypeCode !== 'Automatic' || unit.pumpLocation === 'RemotePump') {
      forEach(unit.manifolds, (manifold, index) => {
        setFieldValue(manifoldFieldName(unitIndex, index, 'pump'), null);
      });
    }
  };

  const handlePumpLocationChange = (newLocationCode: PumpLocationCode) => {
    newLocationCode === 'PumpOnManifold'
      ? setFieldValue(unitFieldName(unitIndex, 'pumps'), [null])
      : forEach(unit.manifolds, (manifold, index) => {
          setFieldValue(manifoldFieldName(unitIndex, index, 'pump'), null);
        });
  };

  return (
    <FormFieldRow>
      {formType === 'Design' && (
        <VerticalCheckboxField name={unitFieldName(unitIndex, 'hasPipelineMap')} label="Using pipeline map?" />
      )}
      <FastTextInputField name={unitFieldName(unitIndex, 'quantity')} label="Quantity" />
      {(unitHasUnderfloorHeating(unit) || unitHasHeatPump(unit)) && (
        <FastSelectField<UnitHeatSourceTypeCode>
          name={unitFieldName(unitIndex, 'heatSourceTypeCode')}
          label="Heat Source"
          options={heatSourceSelectOptions}
        />
      )}
      <FastTextInputField name={unitFieldName(unitIndex, 'numberOfBedrooms')} label="Beds" />
      <FastTextInputField name={unitFieldName(unitIndex, 'numberOfBathrooms')} label="Baths" />
      <CylinderVolumeField />
      {(formType !== 'Design' || !unit.hasPipelineMap) && (
        <>
          <SelectField
            name={unitFieldName(unitIndex, 'pumpSelectionType')}
            label="Pump Selection"
            options={pumpSelectionTypeSelectOptions}
            onChange={handlePumpSelectionTypeChange}
          />
          {unit.pumpSelectionType !== 'PumpsNotSupplied' && (
            <SelectField
              name={unitFieldName(unitIndex, 'pumpLocation')}
              label="Pump Type"
              options={pumpLocationSelectOptions}
              onChange={handlePumpLocationChange}
            />
          )}
          {unit.pumpLocation === 'RemotePump' && unit.pumpSelectionType !== 'PumpsNotSupplied' ? (
            <FieldArray name={unitFieldName(unitIndex, 'pumps')}>
              {arrayHelpers => (
                <MultiplePumpsContainer>
                  {unit.pumps.map((pump, index) => {
                    const pumpError = getIn(errors, `${unitFieldName(unitIndex, 'pumps')}.${index}`);
                    const pumpTouched = getIn(touched, `${unitFieldName(unitIndex, 'pumps')}.${index}`);

                    return (
                      <PumpSelectContainer key={index}>
                        {index > 0 && pumpError != null && pumpTouched && (
                          <ExtraPumpErrorIndicator
                            fieldName={`${unitFieldName(unitIndex, 'pumps')}.${index}`}
                            showError={true}
                            error={pumpError}
                          />
                        )}
                        <SelectField
                          name={`${unitFieldName(unitIndex, 'pumps')}.${index}`}
                          label={index === 0 ? 'Pump' : null}
                          disabled={unit.pumpSelectionType === 'Automatic'}
                          options={unitPumpOptions}
                          mapValueToKey={(value: PumpFormModel | null) => `${value != null ? value.partId : 'null'}`}
                        />
                        {index > 0 && unit.pumpSelectionType === 'UserDefined' && (
                          <DeletePumpButton
                            onClick={() => arrayHelpers.remove(index)}
                            title="Delete pump"
                            data-testid={deleteUnitPumpButtonTestId(unitIndex, index)}
                          >
                            <DeleteIcon />
                          </DeletePumpButton>
                        )}
                      </PumpSelectContainer>
                    );
                  })}
                  {unit.pumpSelectionType === 'UserDefined' && (
                    <SecondaryButton
                      onClick={() => {
                        arrayHelpers.push(null);
                      }}
                      data-testid={addUnitPumpButtonTestId(unitIndex)}
                    >
                      + Add Pump
                    </SecondaryButton>
                  )}
                </MultiplePumpsContainer>
              )}
            </FieldArray>
          ) : null}
        </>
      )}
    </FormFieldRow>
  );
};

const MultiplePumpsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const PumpSelectContainer = styled.div`
  margin-bottom: ${spacing02};
  display: flex;
  align-items: center;

  ${FieldContainer} {
    width: 100%;
  }
`;

const ExtraPumpErrorIndicator = styled(FieldErrorIndicator)`
  margin: 0 ${spacing02} 0 ${spacing01};
`;

const DeletePumpButton = styled(SecondaryButton)`
  width: 40px;
  height: 40px;
  padding: 0 ${spacing01};
  margin-left: ${spacing02};
  align-self: flex-end;
`;
